import React from 'react';
import { Employee, EmployeeWeekStatus, formatEmployeeName } from '../../config';
import './styles.scss';

interface EmployeeListProps {
  employees: Employee[];
  selectedEmployeeId: string | null;
  onEmployeeSelect: (employee: Employee) => void;
  /** Keyed by employee id; drives the status dot and the overdue count. */
  weekStatus?: Record<string, EmployeeWeekStatus>;
  loading?: boolean;
}

const EmployeeList: React.FC<EmployeeListProps> = ({
  employees,
  selectedEmployeeId,
  onEmployeeSelect,
  weekStatus,
  loading = false
}) => {
  // Filter to only show active employees
  const activeEmployees = employees.filter(employee => employee.is_active);

  if (loading) {
    return (
      <div className="employee-list loading">
        <div className="employee-list-header">
          <h3>Employees</h3>
        </div>
        <div className="loading-state">
          <p>Loading employees...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="employee-list">
      <div className="employee-list-header">
        <h3>Employees</h3>
      </div>

      <div className="employee-list-content">
        {activeEmployees.length === 0 ? (
          <div className="no-employees">
            <p>No active employees found</p>
          </div>
        ) : (
          <ul className="employee-items">
            {activeEmployees.map((employee) => {
              const status = weekStatus?.[employee.id];
              const weekState = status?.status ? status.status.toLowerCase() : 'none';
              return (
                <li key={employee.id}>
                  <button
                    className={`employee-item ${selectedEmployeeId === employee.id ? 'selected' : ''}`}
                    onClick={() => onEmployeeSelect(employee)}
                  >
                    <div className="employee-info">
                      <div className="employee-name">
                        <span
                          className={`employee-item__dot employee-item__dot--${weekState}`}
                          title={status?.status ? `This week: ${status.status}` : 'No timesheet this week'}
                          aria-hidden="true"
                        />
                        {formatEmployeeName(employee)}
                      </div>
                      <div className="employee-details">
                        <span className="employee-type">{employee.type}</span>
                      </div>
                    </div>
                    {status && status.overdueCount > 0 && (
                      // Past weeks not submitted: on the row's right edge, clear of the name.
                      <span
                        className="employee-item__overdue"
                        title={`${status.overdueCount} past week${status.overdueCount === 1 ? '' : 's'} not submitted`}
                      >
                        {status.overdueCount}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="employee-list-footer">
        <p className="employee-count">
          {activeEmployees.length} active employees
        </p>
      </div>
    </div>
  );
};

export default React.memo(EmployeeList); 